import axios from 'axios'

/**
 * Single home for HTTP-error-shape knowledge. Screens ask named questions
 * instead of reaching into `error.response.status`, so the axios dependency and
 * every status code the UI reacts to live here and nowhere else.
 */

/** The HTTP status an API error carries, or undefined when there was no response. */
export const statusOf = (error: unknown): number | undefined =>
	axios.isAxiosError(error) ? error.response?.status : undefined

/** The request never got an answer: offline, refused, or timed out. */
export const isNoConnection = (error: unknown): boolean => axios.isAxiosError(error) && !error.response

/** 400 — the request failed server-side validation. */
export const isBadRequest = (error: unknown): boolean => statusOf(error) === 400

/** 404 — the looked-up resource does not exist. */
export const isNotFound = (error: unknown): boolean => statusOf(error) === 404

/** 409 — the change conflicts with current server state. */
export const isConflict = (error: unknown): boolean => statusOf(error) === 409

/** 413 — the upload exceeds the request size limit. */
export const isPayloadTooLarge = (error: unknown): boolean => statusOf(error) === 413

/** 429 — the caller is rate limited. */
export const isRateLimited = (error: unknown): boolean => statusOf(error) === 429

/** 441 — Orbit's non-standard "leaving is blocked by active expenses/pay-ups". */
export const isLeaveBlockedByBalances = (error: unknown): boolean => statusOf(error) === 441

/**
 * The `code` the server puts on the 403 a join answers when the trip has Join
 * approval on. Mirrored from `@orbit/api-types`, which is not browser-safe at
 * runtime; a node test pins the two together.
 */
export const JOIN_REQUEST_PENDING_CODE = 'JoinRequestPending'

/** 403 `JoinRequestPending` — nothing was joined; a Join request now waits on the owner. */
export const isJoinRequestPending = (error: unknown): boolean =>
	statusOf(error) === 403 &&
	axios.isAxiosError(error) &&
	(error.response?.data as { code?: unknown } | undefined)?.code === JOIN_REQUEST_PENDING_CODE

/** Any 4xx — the request itself is at fault, so retrying cannot succeed. */
export const isClientError = (error: unknown): boolean => {
	const status = statusOf(error)
	return status !== undefined && status >= 400 && status < 500
}

/** 5xx — the server failed to handle an otherwise-valid request. */
export const isServerError = (error: unknown): boolean => {
	const status = statusOf(error)
	return status !== undefined && status >= 500
}

/** The HTTP response carried by an API error, in transport-agnostic shape. */
export interface ApiErrorResponse {
	status: number
	data: unknown
	headers: Record<string, unknown>
}

/** The response carried by an API error, or undefined for non-HTTP failures. */
export const getApiErrorResponse = (error: unknown): ApiErrorResponse | undefined => {
	if (axios.isAxiosError(error) && error.response) {
		return {
			status: error.response.status,
			data: error.response.data,
			headers: (error.response.headers ?? {}) as Record<string, unknown>,
		}
	}
	return undefined
}

/**
 * What a failed request says, always in our own words: no answer means check
 * the connection, a 5xx is the server's fault, and anything else is the
 * caller's `defaultMessage`. The server's message is never shown — clients own
 * the copy so it can be localized, and a refusal that needs its own words is
 * told apart by its status or its body `code`, never by its wording.
 */
export const getErrorMessage = (error: unknown, defaultMessage: string): string => {
	if (isNoConnection(error)) return 'Network error. Please check your internet connection and try again.'
	if (isServerError(error)) return 'Server error. Please try again later.'
	return defaultMessage
}

/**
 * A page's line when its data didn't load — "Error loading flights. Please try
 * again." — in our words, like every other error: never the server's message
 * nor axios's ("Request failed with status code 404").
 */
export const loadErrorMessage = (subject: string, error: unknown): string =>
	`Error loading ${subject}. ${getErrorMessage(error, 'Please try again.')}`
